// @dashboard/screenerResults/ScreenerResultsDisplay.js
import React, {useMemo, useState, useEffect} from "react";
import {useScreenerResultsDisplay} from "@dashboard/screenerresultsdisplay/context/ScreenerResultsDisplayContext";
import "./css/ScreenerResultsDisplay.css";

const BUCKET_LABEL = (b) => ({
    GOOD_RES_UNKNOWN_COMPANIES: "Good • Unknown",
    GOOD_RES_SUPERSTAR_HELD: "Good • SS Held",
    GOOD_RES_STAGNENT_COMPANIES: "Good • Stagnant",
    GOOD_RES_MPROFIT_COMPANIES: "Good • MProfit",
    BAD_RES_UNKNOWN_COMPANIES: "Bad • Unknown",
    BAD_RES_SUPERSTAR_HELD: "Bad • SS Held",
    BAD_RES_MPROFIT_COMPANIES: "Bad • MProfit",
}[b] || b);

const FAMILIES = [
    {key: "MPROFIT", label: "MProfit", good: "GOOD_RES_MPROFIT_COMPANIES", bad: "BAD_RES_MPROFIT_COMPANIES"},
    {key: "SSHELD", label: "Superstar Held", good: "GOOD_RES_SUPERSTAR_HELD", bad: null},
    {key: "STAGNANT", label: "Stagnant", good: "GOOD_RES_STAGNENT_COMPANIES", bad: null},
    {key: "UNKNOWN", label: "Unknown", good: "GOOD_RES_UNKNOWN_COMPANIES", bad: null},
];

const hasGood = fam => !!fam.good;
const hasBad = fam => !!fam.bad;

function fmt(n, digits = 2) {
    if (n == null) return "-";
    const x = Number(n);
    return Number.isFinite(x) ? x.toLocaleString("en-IN", {maximumFractionDigits: digits}) : "-";
}

function yoyArrow(pct) {
    if (pct == null) return <span className="yoy neutral">–</span>;
    const n = Number(pct);
    if (!Number.isFinite(n) || n === 0) return <span className="yoy neutral">0%</span>;
    return n > 0
        ? <span className="yoy up">↑ {fmt(n, 0)}%</span>
        : <span className="yoy down">↓ {fmt(Math.abs(n), 0)}%</span>;
}

function qoqArrow(pct) {
    if (pct == null) return <span className="qoq neutral">–</span>;
    const n = Number(pct);
    if (!Number.isFinite(n) || n === 0) return <span className="qoq neutral">0%</span>;
    return n > 0
        ? <span className="qoq up">↑ {fmt(n, 0)}%</span>
        : <span className="qoq down">↓ {fmt(Math.abs(n), 0)}%</span>;
}

const deriveYoY = (curr, last) => {
    if (curr == null || last == null || Number(last) === 0) return null;
    return ((Number(curr) - Number(last)) / Number(last)) * 100.0;
};

const pctPos = (s) => {
    if (!s) return false;
    const n = Number(String(s).replace("%", "").replace(/[^\d.\-]/g, ""));
    return Number.isFinite(n) && n > 0;
};

function HolderChip({holder}) {
    const q = holder.quarters || [];
    return (
        <div className="ss-chip" title={holder.superstarName + " • " + holder.companyName}>
            <div className="ss-name">{holder.superstarName}</div>
            <div className="ss-qtrs">
                {q.map((x, i) => (
                    <div className="ss-col" key={i}>
                        <div className="ss-q">{x.quarterName}</div>
                        <div className={"ss-pct " + (pctPos(x.holdingPct) ? "pos" : "neg")}>
                            {x.holdingPct || "-"}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SuperstarHolders({holders}) {
    const [open, setOpen] = useState(false);
    if (!holders || !holders.length) return null;

    const inlineMax = 4;
    const inline = holders.slice(0, inlineMax);
    const rest = holders.slice(inlineMax);

    return (
        <div className="ss-panel">
            <div className="ss-head">
                {holders.length > inlineMax && (
                    <button className="sr-btn ghost ss-more" onClick={() => setOpen(v => !v)}>
                        {open ? "Hide" : `+${holders.length - inlineMax} more`}
                    </button>
                )}
            </div>
            <div className="ss-list">
                {inline.map((h, idx) => <HolderChip key={idx} holder={h}/>)}
            </div>
            {open && rest.length > 0 && (
                <div className="ss-list ss-list-more">
                    {rest.map((h, idx) => <HolderChip key={`m-${idx}`} holder={h}/>)}
                </div>
            )}
        </div>
    );
}

export default function ScreenerResultsDisplay() {
    const {results, loading, error, filters, markRead, markReadBulk, reload} = useScreenerResultsDisplay();
    const {
        dateMode, setDateMode,
        resultDate, setResultDate,
        dateFrom, setDateFrom,
        dateTo, setDateTo,
        buckets, setBuckets,
        unreadOnly, setUnreadOnly
    } = filters;

    const [activeFamilyKey, setActiveFamilyKey] = useState(FAMILIES[0].key);
    const [activePolarity, setActivePolarity] = useState("GOOD");
    const [exitingIds, setExitingIds] = useState(new Set());
    const [hiddenIds, setHiddenIds] = useState(new Set());

    useEffect(() => {
        const fam = FAMILIES.find(f => f.key === activeFamilyKey) || FAMILIES[0];
        const selectedBucket = (activePolarity === "GOOD") ? fam.good : fam.bad;
        if (!selectedBucket) {
            const fallback = fam.good || fam.bad;
            if (fallback) setBuckets([fallback]);
            return;
        }
        setBuckets([selectedBucket]);
    }, [activeFamilyKey, activePolarity, setBuckets]);

    const filteredResults = useMemo(() => {
        if (!buckets.length) return results;
        return results.filter(r => buckets.includes(r.bucket));
    }, [results, buckets]);

    const handleMarkRead = async (r) => {
        const nextRead = !r.isRead;
        if (nextRead) {
            const ok = await markRead(r.id, true);
            if (!ok) return;

            setExitingIds(prev => {
                const n = new Set(prev);
                n.add(r.id);
                return n;
            });
            setTimeout(() => {
                setExitingIds(prev => {
                    const n = new Set(prev);
                    n.delete(r.id);
                    return n;
                });
                setHiddenIds(prev => {
                    const n = new Set(prev);
                    n.add(r.id);
                    return n;
                });
            }, 320);
        } else {
            const ok = await markRead(r.id, false);
            if (!ok) return;
            setHiddenIds(prev => {
                const n = new Set(prev);
                n.delete(r.id);
                return n;
            });
        }
    };

    // 🔵 mark-all now automatically respects the date filters because it uses filteredResults
    const handleMarkAllShownRead = async () => {
        const visible = filteredResults.filter(r => !hiddenIds.has(r.id));
        const unreadIds = visible.filter(r => !r.isRead).map(r => r.id);
        if (!unreadIds.length) return;

        const ok = await markReadBulk(unreadIds, true);
        if (!ok) return;

        setExitingIds(prev => {
            const n = new Set(prev);
            unreadIds.forEach(id => n.add(id));
            return n;
        });

        setTimeout(() => {
            setExitingIds(prev => {
                const n = new Set(prev);
                unreadIds.forEach(id => n.delete(id));
                return n;
            });
            setHiddenIds(prev => {
                const n = new Set(prev);
                unreadIds.forEach(id => n.add(id));
                return n;
            });
        }, 320);
    };

    const currentFamily = FAMILIES.find(f => f.key === activeFamilyKey);

    return (
        <div className="sr-root">
            <div className="sr-tabs">
                {FAMILIES.map(f => (
                    <button
                        key={f.key}
                        className={`sr-tab ${activeFamilyKey === f.key ? "active" : "inactive"}`}
                        onClick={() => setActiveFamilyKey(f.key)}
                        title={f.label}
                    >
                        {f.label}
                    </button>
                ))}

                <div className="sr-subtabs">
                    <button
                        className={`sr-subtab ${activePolarity === "GOOD" ? "active" : ""} ${hasGood(currentFamily) ? "" : "disabled"}`}
                        onClick={() => hasGood(currentFamily) && setActivePolarity("GOOD")}
                    >
                        Good
                    </button>
                    <button
                        className={`sr-subtab ${activePolarity === "BAD" ? "active" : ""} ${hasBad(currentFamily) ? "" : "disabled"}`}
                        onClick={() => hasBad(currentFamily) && setActivePolarity("BAD")}
                    >
                        Bad
                    </button>

                    <button
                        className="sr-btn sr-bulk-btn"
                        onClick={handleMarkAllShownRead}
                        disabled={filteredResults.filter(r => !hiddenIds.has(r.id) && !r.isRead).length === 0}
                    >
                        Mark all shown read
                    </button>
                </div>

                <div className="sr-toolbar-block">
                    {/* row 1 */}
                    <div className="sr-toolbar sr-toolbar-inline sr-toolbar-row1">
                        <div className="sr-filter sr-date-filter" data-mode={dateMode}>
                            <label>Date:</label>
                            <select
                                value={dateMode}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDateMode(v);
                                    if (v === "single") {
                                        setResultDate("");
                                        // keep from/to but they won't show
                                    } else {
                                        setResultDate("");
                                    }
                                }}
                            >
                                <option value="single">Single</option>
                                <option value="range">Range</option>
                            </select>

                            {/* slot that is ALWAYS the same width */}
                            <div className="sr-date-slot">
                                {dateMode === "single" ? (
                                    <input
                                        type="date"
                                        value={resultDate}
                                        onChange={(e) => setResultDate(e.target.value)}
                                    />
                                ) : (
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        placeholder="From"
                                    />
                                )}
                            </div>
                        </div>

                        <button
                            className="sr-btn ghost"
                            onClick={() => {
                                if (dateMode === "single") {
                                    setResultDate("");
                                } else {
                                    setDateFrom("");
                                    setDateTo("");
                                }
                            }}
                        >
                            Clear
                        </button>

                        <div className="sr-filter">
                            <label className="checkbox">
                                <input
                                    type="checkbox"
                                    checked={unreadOnly}
                                    onChange={e => setUnreadOnly(e.target.checked)}
                                />
                                Unread only
                            </label>
                        </div>

                        <div className="sr-actions">
                            <button className="sr-btn" onClick={reload}>
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* row 2: only shows in range mode */}
                    {dateMode === "range" && (
                        <div className="sr-toolbar-row2">
                            <div className="sr-toolbar-row2-spacer" aria-hidden="true"></div>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                placeholder="To"
                            />
                        </div>
                    )}
                </div>

            </div>


            {loading && <div className="sr-status">Loading results…</div>}
            {error && <div className="sr-error">⚠ {error}</div>}

            <div className="sr-scope">
                <div className="sr-list">
                    {filteredResults
                        .filter(r => !hiddenIds.has(r.id))
                        .map(r => (
                            <div
                                key={r.id}
                                className={`sr-card ${r.isRead ? "read" : "unread"} ${exitingIds.has(r.id) ? "sr-card-exit" : ""}`}
                            >
                                <div className="sr-card-head">
                                    <div className="sr-title">
                                        <a href={r.companyScreenerUrl || '#'} target="_blank" rel="noreferrer">
                                            {r.companyName || r.symbol}
                                        </a>
                                        <span className="sr-sub">{r.symbol}</span>
                                        <span className="sr-chip">{BUCKET_LABEL(r.bucket)}</span>
                                        <span className="sr-date">{r.resultDate}</span>
                                    </div>
                                    <div className="sr-head-actions">
                                        <button
                                            className={`sr-btn ${r.isRead ? "outline" : ""}`}
                                            onClick={() => handleMarkRead(r)}
                                        >
                                            {r.isRead ? "Mark Unread" : "Mark Read"}
                                        </button>
                                    </div>
                                </div>

                                <div className="sr-content">
                                    <div className="sr-table">
                                        <div className="sr-row sr-headrow">
                                            <div className="c-1"></div>
                                            <div className="c-2">YOY</div>
                                            <div className="c-3">QOQ</div>
                                            <div className="c-4">Current</div>
                                            <div className="c-5">Prev Q</div>
                                            <div className="c-6">Last Yr Q</div>
                                        </div>
                                        <div className="sr-row">
                                            <div className="c-1">Sales</div>
                                            <div className="c-2">{yoyArrow(r.salesGrowthYoY)}</div>
                                            <div className="c-3">{qoqArrow(r.salesGrowthQoQ)}</div>
                                            <div className="c-4">{fmt(r.currentQSales)}</div>
                                            <div className="c-5">{fmt(r.prevQSales)}</div>
                                            <div className="c-6">{fmt(r.lastYearQSales)}</div>
                                        </div>
                                        <div className="sr-row">
                                            <div className="c-1">EBIDT</div>
                                            <div className="c-2">{yoyArrow(r.opGrowthYoY)}</div>
                                            <div className="c-3">{qoqArrow(r.opGrowthQoQ)}</div>
                                            <div className="c-4">{fmt(r.currentQOperProfit)}</div>
                                            <div className="c-5">{fmt(r.prevQOperProfit)}</div>
                                            <div className="c-6">{fmt(r.lastYearQOperProfit)}</div>
                                        </div>
                                        <div className="sr-row">
                                            <div className="c-1">Net profit</div>
                                            <div
                                                className="c-2">{yoyArrow(r.npGrowthYoY)}</div>
                                            <div className="c-3">{qoqArrow(r.npGrowthQoQ)}</div>
                                            <div className="c-4">{fmt(r.currentQNetProfit)}</div>
                                            <div className="c-5">{fmt(r.prevQNetProfit)}</div>
                                            <div className="c-6">{fmt(r.lastYearQNetProfit)}</div>
                                        </div>
                                        <div className="sr-row">
                                            <div className="c-1">EPS</div>
                                            <div
                                                className="c-2">{yoyArrow(r.epsGrowthYoY)}</div>
                                            <div className="c-3">{qoqArrow(r.epsGrowthQoQ)}</div>
                                            <div className="c-4">₹ {fmt(r.currentQEPs, 2)}</div>
                                            <div className="c-5">₹ {fmt(r.prevQEPs, 2)}</div>
                                            <div className="c-6">₹ {fmt(r.lastYearQEPs, 2)}</div>
                                        </div>
                                    </div>
                                    <div className="sr-right">
                                        <SuperstarHolders holders={r.superstarHolders}/>
                                    </div>
                                </div>
                            </div>
                        ))}
                    {!loading && !filteredResults.length && <div className="sr-empty">No results.</div>}
                </div>
            </div>
        </div>
    );
}
